/**
 * Hook for zoomable value-axis session ranges (Y and scatter X) in parent-controlled mode.
 *
 * Preferences define defaults, session state holds live zoom until reset.
 *
 * @packageDocumentation
 * @module useAxisSessionRanges
 * @public
 */

import { useCallback, useState } from 'react';
import { AxisKey, PlotPreferencesV2 } from '../config/plotTypes';
import {
  AxisSessionRanges,
  clearZoomableAxisSessionRangesForPlot,
} from './axisSessionLayoutState';

// -- type definitions --

/** Options for {@link useAxisSessionRanges}. */
export interface UseAxisSessionRangesOptions {
  plotPreferences: PlotPreferencesV2;
  plotType: 'timeseries' | 'scatter';
}

/** Result of {@link useAxisSessionRanges}. */
export interface UseAxisSessionRangesResult {
  /** Live zoom on value axes, keyed by axis key. */
  axisSessionRanges: AxisSessionRanges;
  /** Pass to {@link ScatterPlotComponent} as `onChangeAxisRange`. */
  onChangeAxisRange: (
    axisKey: AxisKey,
    range: [number, number] | [string, string] | undefined
  ) => void;
  /** Clear one axis session range without touching other axes. */
  clearAxisRange: (axisKey: AxisKey) => void;
  /** Clear zoom on zoom-enabled value axes (e.g. on plot double-click). */
  resetZoomableAxes: () => void;
}

// -- helper functions --

/**
 * Parse a numeric axis range.
 */
function parseNumericAxisRange(
  range: [number, number] | [string, string]
): [number, number] | undefined {
  const start = Number(range[0]);
  const end = Number(range[1]);

  if (Number.isNaN(start) || Number.isNaN(end)) {
    return undefined;
  }

  return [start, end];
}

/**
 * Manage session zoom for zoomable value axes in controlled
 * {@link ScatterPlotComponent} mode.
 */
export function useAxisSessionRanges({
  plotPreferences,
  plotType,
}: UseAxisSessionRangesOptions): UseAxisSessionRangesResult {

  /** Session zoom ranges for zoomable value axes, keyed by {@link AxisKey}. */
  const [axisSessionRanges, setAxisSessionRanges] = useState<AxisSessionRanges>(
    {}
  );

  /** 
   * Callback for when the range of a zoomable axis changes.
   * 
   * Updates the {@link axisSessionRanges} state.
   */
  const onChangeAxisRange = useCallback(
    (
      axisKey: AxisKey,
      range: [number, number] | [string, string] | undefined
    ) => {
      if (range === undefined) {
        setAxisSessionRanges((prev) => {
          if (!(axisKey in prev)) {
            return prev;
          }

          const next = { ...prev };
          delete next[axisKey];
          return next;
        });
        return;
      }

      const numericRange = parseNumericAxisRange(range);
      if (!numericRange) {
        return;
      }

      setAxisSessionRanges((prev) => ({
        ...prev,
        [axisKey]: numericRange,
      }));
    },
    []
  );

  /**
   * Clear one axis range from session state.
   */
  const clearAxisRange = useCallback((axisKey: AxisKey) => {
    onChangeAxisRange(axisKey, undefined);
  }, [onChangeAxisRange]);

  /**
   * Clear the zoomable axes in the {@link axisSessionRanges} state.
   */
  const resetZoomableAxes = useCallback(() => {
    setAxisSessionRanges((prev) =>
      clearZoomableAxisSessionRangesForPlot(prev, plotPreferences, plotType)
    );
  }, [plotPreferences, plotType]);

  // -- return values --

  return {
    axisSessionRanges,
    onChangeAxisRange,
    clearAxisRange,
    resetZoomableAxes,
  };
}
